/* eslint-disable @typescript-eslint/explicit-function-return-type */

import { FC, memo, useMemo } from 'react';

import { TbArrowsUpDown, TbNotebook } from 'react-icons/tb';
import { SiMarketo } from 'react-icons/si';
import {
  MdDevices,
  MdEvent,
  MdLocationOn,
  MdOutlineDashboardCustomize,
  MdOutlineMenuBook,
  MdVerifiedUser,
} from 'react-icons/md';
import { LiaFileImportSolid } from 'react-icons/lia';
import {
  IoBusiness,
  IoCalculator,
  IoHome,
  IoLayers,
  IoPeople,
  IoPricetag,
  IoCart,
  IoPricetags,
  IoCopy,
} from 'react-icons/io5';
import { IoIosPeople } from 'react-icons/io';
import { HiSpeakerphone, HiUpload } from 'react-icons/hi';
import { FiMonitor } from 'react-icons/fi';
import { FaTableList } from 'react-icons/fa6';
import {
  FaBarcode,
  FaBoxes,
  FaCashRegister,
  FaCube,
  FaFileInvoiceDollar,
  FaHandHoldingUsd,
  FaIndustry,
  FaListUl,
  FaSatelliteDish,
  FaTruck,
} from 'react-icons/fa';
import { BsFillLightningChargeFill, BsTable, BsDownload } from 'react-icons/bs';
import {
  BiNotepad,
  BiSolidCategory,
  BiSolidDoorOpen,
  BiTransfer,
} from 'react-icons/bi';
import {
  AiFillDollarCircle,
  AiFillWallet,
  AiOutlineAudit,
} from 'react-icons/ai';
import { IconType } from 'react-icons';
import { CreateToastFnReturn } from '@chakra-ui/react';

import { validateUserPermissions } from '~/shared/utils/validateUserPermissions';
import { redirectToExternalAppWithToken } from '~/shared/services/redirectToken';
import { setupApi } from '~/shared/services/api';
import { useDynamicSidebar } from '~/shared/hooks/dynamicSidebar';
import { SidebarPagesEnum } from '~/shared/enums/SidebarPagesEnum';
import { SidebarGroupsEnum } from '~/shared/enums/SidebarGroupsEnum';
import { ReferenceIdEnum } from '~/shared/enums/ReferenceIdEnum';
import { ExternalAppsEnum } from '~/shared/enums/ExternalAppsEnum';
import { routes } from '~/shared/constants/routes';
import { permissions } from '~/shared/constants/permissions';
import { useAuth } from '~/modules/auth/hooks/auth';

import { ColibriIcon } from '../../../../../public/images/logos/colibri';

import { Container } from './styles';
import { NavSection } from './NavSection';
import { NavItem } from './NavItem';

const redirectToColibriPay = async (toast?: CreateToastFnReturn) => {
  try {
    const { data } = await setupApi().get<{ redirectUrl: string }>(
      '/apps/colibri-pay/url'
    );

    window.open(data.redirectUrl, '_blank');
  } catch {
    toast?.({
      title: 'Ops!',
      description: `Não foi possível redirecionar para o Colibri Pay.`,
      status: 'error',
    });
  }
};

interface DynamicTabProps {
  icon: IconType;
  href?: string;
  referenceId: ReferenceIdEnum;
  permissions?: Array<string>;
  shouldMatchExactHref?: boolean;
  comingSoon?: boolean;
  isNew?: boolean;
  isDisabled?: boolean;
  hint?: string;
  tag?: string;
  onClick?: (toast?: CreateToastFnReturn) => void | Promise<void>;
}

interface Tab extends DynamicTabProps {
  label: string;
}

interface NavSectionProps {
  title?: string;
  shouldTabsStartsVisible?: boolean;
  tabs: Tab[];
  tag: string;
}
export const tabProps: { [key: string]: DynamicTabProps } = {
  [SidebarPagesEnum.HOME]: {
    icon: IoHome,
    href: routes.home,
    referenceId: ReferenceIdEnum.home,
  },
  [SidebarPagesEnum.LIST_COMPANIES]: {
    icon: IoBusiness,
    href: routes.companies.list,
    referenceId: ReferenceIdEnum.companies,
    permissions: [permissions.companies.list],
  },
  [SidebarPagesEnum.USER_GROUPS]: {
    icon: MdVerifiedUser,
    href: routes.users.groups.list,
    referenceId: ReferenceIdEnum['user-groups'],
    permissions: [permissions.groups.list],
    shouldMatchExactHref: true,
  },
  [SidebarPagesEnum.LIST_USERS]: {
    icon: IoPeople,
    href: routes.users.list,
    referenceId: ReferenceIdEnum.users,
    permissions: [permissions.users.list],
    shouldMatchExactHref: true,
  },
  [SidebarPagesEnum.LIST_MARKETPLACE_MANAGEMENT]: {
    icon: FaSatelliteDish,
    href: routes.marketplacesManagement.list(),
    referenceId: ReferenceIdEnum['marketplaces-management'],
  },
  [SidebarPagesEnum.LIST_ORDERS]: {
    icon: AiFillDollarCircle,
    href: routes.orders.list,
    referenceId: ReferenceIdEnum.orders,
    permissions: [permissions.orders.list],
    shouldMatchExactHref: true,
  },
  [SidebarPagesEnum.POST_ORDERS]: {
    tag: 'orders-posts',
    icon: FaTruck,
    href: routes.ordersPosts.list,
    referenceId: ReferenceIdEnum['orders-posts'],
  },
  [SidebarPagesEnum.LIST_CASHIERS]: {
    referenceId: ReferenceIdEnum.cashiers,
    icon: FaCashRegister,
    href: routes.cashiers.list,
    permissions: [permissions.cashiers.list],
  },
  [SidebarPagesEnum.CASHIER_TRANSACTIONS_BATCHES]: {
    referenceId: ReferenceIdEnum['cashier-transactions-batches'],
    icon: FaTruck,
    href: routes.cashierTransactionsBatches.list,
    permissions: [permissions.cashierTransactionsBatches.list],
  },
  [SidebarPagesEnum.CASHIERS_REPORT]: {
    referenceId: ReferenceIdEnum['cashiers-reports'],
    icon: BiNotepad,
    href: routes.cashiers.reports,
    isNew: false,
  },
  [SidebarPagesEnum.LIST_CUSTOMERS]: {
    referenceId: ReferenceIdEnum.customers,
    icon: IoPeople,
    href: routes.participants.customer.list,
    permissions: [permissions.customer.list],
  },
  [SidebarPagesEnum.LIST_SELLERS]: {
    referenceId: ReferenceIdEnum.sellers,
    icon: IoIosPeople,
    href: routes.sellers.list,
    isNew: false,
    permissions: [permissions.sellers.list],
  },
  [SidebarPagesEnum.ABANDONED_CARTS]: {
    referenceId: ReferenceIdEnum['abandoned-carts'],
    icon: IoCart,
    href: routes.abandonedCarts.list,
    comingSoon: true,
    isDisabled: true,
  },
  [SidebarPagesEnum.ORDERS_REPORT]: {
    referenceId: ReferenceIdEnum['orders-reports'],
    icon: BiNotepad,
    href: routes.sales.reports,
    isNew: false,
  },
  [SidebarPagesEnum.LIST_PRODUCTS]: {
    referenceId: ReferenceIdEnum.products,
    icon: IoPricetag,
    href: routes.products.list,
    permissions: [permissions.products.list],
    shouldMatchExactHref: true,
  },
  [SidebarPagesEnum.LIST_CATEGORIES]: {
    referenceId: ReferenceIdEnum.categories,
    icon: IoLayers,
    href: routes.categories.list,
    permissions: [permissions.categories.list],
  },
  [SidebarPagesEnum.LIST_CLASSES]: {
    referenceId: ReferenceIdEnum.classes,
    icon: BiSolidCategory,
    href: routes.classes.list,
    permissions: [permissions.classes.list],
  },
  [SidebarPagesEnum.LIST_COMBOS]: {
    referenceId: ReferenceIdEnum.combos,
    icon: IoPricetags,
    href: routes.combos.list,
    permissions: [permissions.combos.list],
  },
  [SidebarPagesEnum.LIST_MATERIAL_GROUPS]: {
    referenceId: ReferenceIdEnum['material-groups'],
    icon: IoCopy,
    href: routes.materialGroups.list,
    permissions: [permissions.materialGroups.get],
  },
  [SidebarPagesEnum.LIST_CATALOGS]: {
    referenceId: ReferenceIdEnum.catalogs,
    icon: MdOutlineMenuBook,
    href: routes.catalogs.list,
    permissions: [permissions.catalogs.list],
  },
  [SidebarPagesEnum.LIST_BRANDS]: {
    referenceId: ReferenceIdEnum.brands,
    icon: IoPricetag,
    href: routes.brands.list,
  },
  [SidebarPagesEnum.LIST_UNIT_TYPES]: {
    referenceId: ReferenceIdEnum['unit-types'],
    icon: FaTableList,
    href: routes.unitTypes.list,
    permissions: [permissions.unitTypes.list],
  },
  [SidebarPagesEnum.LIST_ENVIRONMENTS]: {
    referenceId: ReferenceIdEnum['product-environments'],
    icon: MdLocationOn,
    href: routes.products.environments.list,
  },
  [SidebarPagesEnum.LIST_TAXATION_RULES]: {
    referenceId: ReferenceIdEnum['taxation-rules'],
    icon: IoCalculator,
    href: routes.taxationRules.list,
    permissions: [permissions.taxationRules.list],
  },
  [SidebarPagesEnum.PRODUCTS_REPORTS]: {
    referenceId: ReferenceIdEnum['products-reports'],
    icon: BiNotepad,
    href: routes.products.reports,
    isNew: false,
  },
  [SidebarPagesEnum.LIST_PROMOTIONS]: {
    referenceId: ReferenceIdEnum.promotions,
    icon: HiSpeakerphone,
    href: routes.promotions.list,
    comingSoon: false,
    isDisabled: false,
    isNew: false,
  },
  [SidebarPagesEnum.LIST_STOCKLOCALS]: {
    referenceId: ReferenceIdEnum['stock-locals'],
    icon: FaBoxes,
    href: routes.stockLocals.list,
    shouldMatchExactHref: true,
  },
  [SidebarPagesEnum.LIST_STOCK_ADJUSTMENTS]: {
    referenceId: ReferenceIdEnum['stock-adjustment'],
    icon: FaCube,
    href: routes.stockAdjustments.list(),
    permissions: [permissions.stockAdjustments.list],
  },
  [SidebarPagesEnum.LIST_STOCK_MONITORING]: {
    referenceId: ReferenceIdEnum['stock-monitoring'],
    icon: FiMonitor,
    href: routes.stockMonitoring.list,
  },
  [SidebarPagesEnum.LIST_STOCK_TRANSFER]: {
    referenceId: ReferenceIdEnum['stock-transfer-group'],
    icon: BiTransfer,
    href: routes.stockTransferGroups.list,
    permissions: [permissions.stockTransferGroups.list],
  },
  [SidebarPagesEnum.LIST_STOCK_CONFERENCES]: {
    referenceId: ReferenceIdEnum['stock-conferences'],
    icon: TbNotebook,
    href: routes.stockConferences.list,
    permissions: [permissions.stockConferences.list],
  },
  [SidebarPagesEnum.STOCKLOCALS_REPORTS]: {
    referenceId: ReferenceIdEnum['stock-reports'],
    icon: BiNotepad,
    href: routes.stockLocals.reports,
    isNew: false,
    shouldMatchExactHref: true,
  },
  [SidebarPagesEnum.LIST_INTEGRATIONS]: {
    referenceId: ReferenceIdEnum.integrations,
    icon: SiMarketo,
    href: routes.apps.list(),
  },
  [SidebarPagesEnum.LIST_EVENTS]: {
    referenceId: ReferenceIdEnum.events,
    icon: MdEvent,
    href: routes.events.list,
    permissions: [permissions.events.list],
  },
  [SidebarPagesEnum.FILE_TRANSMISSIONS]: {
    referenceId: ReferenceIdEnum['file-transmissions'],
    icon: HiUpload,
    href: routes.fileTransmissions.board(),
  },
  [SidebarPagesEnum.LIST_DEVICES]: {
    referenceId: ReferenceIdEnum.devices,
    icon: MdDevices,
    href: routes.devices.list,
  },
  [SidebarPagesEnum.LIST_LOANS]: {
    referenceId: ReferenceIdEnum.loans,
    icon: FaHandHoldingUsd,
    href: routes.banking.loans.list,
    isNew: false,
  },
  [SidebarPagesEnum.LIST_LAST_TRANSACTIONS]: {
    referenceId: ReferenceIdEnum['last-transactions'],
    icon: AiFillWallet,
    href: routes.banking.transactions.list,
    comingSoon: true,
    isDisabled: true,
  },
  [SidebarPagesEnum.RECHARGES]: {
    referenceId: ReferenceIdEnum.recharges,
    icon: BsFillLightningChargeFill,
    href: routes.banking.recharges.list,
    comingSoon: true,
    isDisabled: true,
  },
  [SidebarPagesEnum.BILL_PAYMENTS]: {
    referenceId: ReferenceIdEnum['bill-payments'],
    icon: FaBarcode,
    href: routes.banking.billPayments.list,
    comingSoon: true,
    isDisabled: true,
  },
  [SidebarPagesEnum.PRODUCTS_BATCH_UPDATE]: {
    referenceId: ReferenceIdEnum['products-batch-update'],
    icon: TbArrowsUpDown,
    href: routes.products.batchUpdate,
    isNew: false,
  },
  [SidebarPagesEnum.PRODUCTS_PRICE_LIST]: {
    referenceId: ReferenceIdEnum['products-price-list'],
    icon: BsTable,
    href: routes.products.priceLists,
    comingSoon: false,
    isDisabled: false,
  },
  [SidebarPagesEnum.PRODUCTS_IMPORT]: {
    referenceId: ReferenceIdEnum['products-import'],
    icon: BsDownload,
    href: routes.products.productsImport,
    comingSoon: false,
    isDisabled: false,
  },
  [SidebarPagesEnum.PERMISSIONS_GRANTED]: {
    referenceId: ReferenceIdEnum['permissions-granted'],
    icon: AiOutlineAudit,
    href: routes.audits.permissionsGranted.list,
  },
  [SidebarPagesEnum.CUSTOM_FIELDS]: {
    referenceId: ReferenceIdEnum['custom-fields'],
    icon: MdOutlineDashboardCustomize,
    href: routes.customFields.list,
    permissions: [permissions.customFields.list],
  },
  [SidebarPagesEnum.FINANCIAL_TITLES]: {
    referenceId: ReferenceIdEnum['financial-titles'],
    icon: BiSolidDoorOpen,
    href: routes.financialTitles.list,
    permissions: [permissions.financialTitles.list],
  },
  [SidebarPagesEnum.BANK_ACCOUNTS]: {
    referenceId: ReferenceIdEnum['bank-accounts'],
    icon: BiSolidDoorOpen,
    href: routes.bankAccounts.list,
    isNew: true,
    permissions: [permissions.bankAccounts.list],
  },
  [SidebarPagesEnum.INBOUND_INVOICES]: {
    referenceId: ReferenceIdEnum['inbound-invoices'],
    icon: LiaFileImportSolid,
    href: routes.invoices.inbound.list,
    permissions: [permissions.invoices.list],
  },
  [SidebarPagesEnum.OUTBOUND_INVOICES]: {
    referenceId: ReferenceIdEnum['outbound-invoices'],
    icon: FaFileInvoiceDollar,
    href: routes.invoices.outbound.list,
    permissions: [permissions.outboundInvoices.list],
  },
  [SidebarPagesEnum.SUPPLIERS]: {
    referenceId: ReferenceIdEnum['suppliers'],
    icon: FaIndustry,
    href: routes.participants.supplier.list,
    permissions: [permissions.supplier.list],
  },
  [SidebarPagesEnum.ACCOUNT_PLANS]: {
    referenceId: ReferenceIdEnum['account-plans'],
    icon: FaListUl,
    href: routes.accountPlans.list,
    permissions: [permissions.accountPlans.list],
  },
  [SidebarPagesEnum.CARRIERS]: {
    referenceId: ReferenceIdEnum['carriers'],
    icon: FaTruck,
    href: routes.participants.carrier.list,
    permissions: [permissions.carrier.list],
  },
  [SidebarPagesEnum.APP_GESTAO]: {
    referenceId: ReferenceIdEnum['app-gestao'],
    icon: ColibriIcon,
    isNew: true,
    onClick: async (toast) =>
      await redirectToExternalAppWithToken(ExternalAppsEnum.APP_GESTAO, toast),
    permissions: [permissions.appGestao.list],
  },
  [SidebarPagesEnum.COLIBRIPAY]: {
    referenceId: ReferenceIdEnum['colibri-pay'],
    icon: ColibriIcon,
    isNew: true,
    onClick: async (toast) => await redirectToColibriPay(toast),
    permissions: [permissions.colibriPay.list],
  },
  [SidebarPagesEnum.COLIBRI_DELIVERY]: {
    referenceId: ReferenceIdEnum['colibri-delivery'],
    icon: ColibriIcon,
    isNew: true,
    onClick: async (toast) =>
      await redirectToExternalAppWithToken(
        ExternalAppsEnum.SERU_DELIVERY,
        toast
      ),
  },
};

export const SideBarNav: FC = memo(() => {
  const { user } = useAuth();
  const { sidebarData } = useDynamicSidebar();

  const hasOnBoarding = true;

  const sortItems = (a, b): number =>
    a.order === b.order ? 0 : a.order < b.order ? -1 : 1;

  const dynamicSectionList: Array<NavSectionProps> = useMemo(
    () =>
      sidebarData
        ?.sort(sortItems)
        .map((sidebarItem) => {
          const { group: tag, customTitle: title, items: data } = sidebarItem;
          const tabs: Tab[] =
            data
              .sort(sortItems)
              .filter((tab) => tab.status === 'active')
              ?.map((sidebarTab) => ({
                label: sidebarTab.customTitle,
                hint:
                  sidebarTab._id === SidebarPagesEnum.LIST_ENVIRONMENTS
                    ? 'Locais de produção'
                    : undefined,
                ...tabProps[sidebarTab._id],
              })) || [];

          if (
            tag === SidebarGroupsEnum.products &&
            !tabs.some(
              (tab) => tab.referenceId === ReferenceIdEnum['unit-types']
            )
          ) {
            tabs.push({
              label: 'Unidades de Medida',
              ...tabProps[SidebarPagesEnum.LIST_UNIT_TYPES],
            });
          }

          const shouldTabsStartsVisible =
            tag === 'configurations' || tag === 'stock' ? hasOnBoarding : true;

          return {
            tag,
            title,
            shouldTabsStartsVisible,
            tabs,
          };
        })
        .filter((group) => group.tabs?.length > 0) || [],
    [hasOnBoarding, sidebarData]
  );

  return (
    <Container>
      {dynamicSectionList.map((item) => {
        const doesntHaveAllPermissions = item.tabs?.every(
          (tab) =>
            !validateUserPermissions({
              user,
              permissions: tab.permissions,
            })
        );

        if (doesntHaveAllPermissions) return null;

        return (
          <NavSection
            key={item.tag}
            tag={item.tag}
            title={item.title}
            tabsStartsVisible
          >
            {item.tabs
              .filter((tab) => tab.href || tab.onClick)
              .map((tab) => {
                const isWithValidPermissions = validateUserPermissions({
                  user,
                  permissions: tab.permissions,
                });

                if (!isWithValidPermissions) return null;

                return (
                  <NavItem
                    key={tab.referenceId}
                    id={tab.referenceId}
                    isDisabled={tab.isDisabled}
                    comingSoon={tab.comingSoon}
                    isNew={tab.isNew}
                    hint={tab.hint}
                    shouldMatchExactHref={tab.shouldMatchExactHref}
                    icon={tab.icon}
                    href={tab.href}
                    onClickAction={tab.onClick}
                  >
                    {tab.label}
                  </NavItem>
                );
              })}
          </NavSection>
        );
      })}
    </Container>
  );
});
